import { EMapProvider } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { Fragment, PureComponent, ReactNode } from "react";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSwitch from "../../../../ui-kit/kuika-switch/kuika-switch";
import { ConfigField } from "../config-pane-parts";

/** A fixed pair, so it is a constant rather than something fetched. */
const MAP_PROVIDERS: KuikaSelectboxOption[] = [
  { value: EMapProvider.GoogleMaps, label: "Google Maps" },
  { value: EMapProvider.Amap, label: "Amap" }
];

/** Both providers, Google first: the order the inputs render in when the end user may choose. */
const ALL_MAP_PROVIDERS: EMapProvider[] = [EMapProvider.GoogleMaps, EMapProvider.Amap];

export interface IConfigMapProviderSelectProps {
  /** Parent config section this selector belongs to: "webConfig" | "androidConfig" | "apiConfig". */
  parentData: string;
  /** Optional tooltip localization key shown next to the provider dropdown. */
  tooltipContentKey?: string;
  /**
   * What the row is called. "Provider" says enough inside a Maps section; among MCP's
   * thirteen provider rows it says nothing, so that caller names it.
   */
  label?: string;
  /**
   * Offers the "let end users choose" switch (`[parentData, "allowEndUserMapProviderChoice"]`).
   * Only the web and Android sections render maps, so only they pass it.
   */
  supportsEndUserChoice?: boolean;
  /** What enabling the choice costs on this target, shown under the switch while it is on. */
  endUserChoiceHint?: string;
  /** Renders the provider-specific settings input for the given provider. */
  renderProvider: (provider: EMapProvider) => ReactNode;
}

interface IMapProviderSectionValues {
  mapProvider?: EMapProvider;
  allowEndUserMapProviderChoice?: boolean;
  amapSettingsId?: string | null;
}

const sectionOf = (values: unknown, parentData: string): IMapProviderSectionValues | undefined =>
  (values as Record<string, IMapProviderSectionValues | undefined> | undefined)?.[parentData];

/**
 * Renders the map-provider dropdown (Google Maps / Amap) for a config section and, below it,
 * the settings input for whichever provider is selected. The provider is persisted through the
 * enclosing config form under `[parentData, "mapProvider"]`.
 *
 * With `supportsEndUserChoice`, a switch lets the generated app bundle BOTH providers so its end
 * users pick one at runtime (the SetMapProvider action). While it is on the dropdown is only the
 * default, both providers' settings inputs render, and code generation requires an Amap setting —
 * the warning says so before generation fails on it.
 *
 * Backward compatibility: existing configs have no persisted `mapProvider`. `getValueProps`
 * displays Google Maps for an absent value WITHOUT writing it to the form store, so untouched
 * configs are neither mutated nor marked dirty (the enclosing form's dirty-tracking normalizes
 * an absent provider to Google Maps).
 */
export class ConfigMapProviderSelect extends PureComponent<IConfigMapProviderSelectProps> {
  render(): ReactNode {
    const {
      parentData,
      tooltipContentKey,
      renderProvider,
      label = "Provider",
      supportsEndUserChoice = false,
      endUserChoiceHint
    } = this.props;
    const mapProviderFieldName: string[] = [parentData, "mapProvider"];
    const choiceFieldName: string[] = [parentData, "allowEndUserMapProviderChoice"];

    return (
      <Form.Item
        noStyle
        shouldUpdate={(prev: unknown, cur: unknown): boolean => {
          const before: IMapProviderSectionValues | undefined = sectionOf(prev, parentData);
          const after: IMapProviderSectionValues | undefined = sectionOf(cur, parentData);
          return (
            before?.mapProvider !== after?.mapProvider ||
            before?.allowEndUserMapProviderChoice !== after?.allowEndUserMapProviderChoice ||
            before?.amapSettingsId !== after?.amapSettingsId
          );
        }}
      >
        {({ getFieldValue }) => {
          const provider: EMapProvider = getFieldValue(mapProviderFieldName) ?? EMapProvider.GoogleMaps;
          const isChoiceEnabled: boolean = supportsEndUserChoice && getFieldValue(choiceFieldName) === true;
          const amapSettingsId: string | null | undefined = getFieldValue([parentData, "amapSettingsId"]);
          const choiceWarning: string | undefined =
            isChoiceEnabled && !amapSettingsId
              ? "Select an Amap setting below: code generation fails without one while end users can choose."
              : undefined;
          const shownProviders: EMapProvider[] = isChoiceEnabled ? ALL_MAP_PROVIDERS : [provider];

          return (
            <>
              {supportsEndUserChoice && (
                <ConfigField hint={isChoiceEnabled ? endUserChoiceHint : undefined} warning={choiceWarning}>
                  <Form.Item name={choiceFieldName} valuePropName="checked" noStyle>
                    <KuikaSwitch
                      label="Let end users choose the map provider"
                      dataTestId={`${parentData}MapProviderChoiceSwitch`}
                    />
                  </Form.Item>
                </ConfigField>
              )}
              <ConfigField label={isChoiceEnabled ? "Default provider" : label} tooltipContentKey={tooltipContentKey}>
                <Form.Item
                  name={mapProviderFieldName}
                  getValueProps={(value) => ({ value: value ?? EMapProvider.GoogleMaps })}
                >
                  <KuikaSelectbox size="md" data-testid={`${parentData}MapProviderDropdown`} options={MAP_PROVIDERS} />
                </Form.Item>
              </ConfigField>
              {shownProviders.map((shown: EMapProvider): ReactNode => (
                <Fragment key={shown}>{renderProvider(shown)}</Fragment>
              ))}
            </>
          );
        }}
      </Form.Item>
    );
  }
}
